import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, UserPlus, UserMinus, Loader2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const ROLE_LABEL = { crew: 'Crew', pm: 'PM', dispatch: 'Dispatch / office', crib: 'Tool crib' };

// Who's in this job chat — everyone can look; office / dispatch / PMs can add or remove people.
export default function ChatParticipants({ thread, isMgmt, myId, onClose, onChanged }) {
  const [q, setQ] = useState('');
  const [people, setPeople] = useState(null);
  const [busy, setBusy] = useState(null);
  const parts = thread.participants || [];
  const inChat = new Set(parts.map((p) => p.id));

  useEffect(() => {
    if (!isMgmt) return;
    const t = setTimeout(() => {
      axios.get(`${API}/dispatch-chat/people`, { ...auth(), params: { q } }).then((r) => setPeople(r.data)).catch(() => setPeople([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, isMgmt]);

  const add = async (u) => {
    setBusy(u.id);
    try { await axios.post(`${API}/dispatch-chat/threads/${thread.id}/participants`, { user_id: u.id }, auth()); toast.success(`${u.name} added`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
    setBusy(null);
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} from this chat? They'll stop getting messages for this job.`)) return;
    setBusy(p.id);
    try {
      const r = await axios.delete(`${API}/dispatch-chat/threads/${thread.id}/participants/${p.id}`, auth());
      toast.success(`${p.name} removed`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
    setBusy(null);
  };

  const candidates = (people || []).filter((u) => !inChat.has(u.id)).slice(0, 12);

  return (
    <div className="absolute inset-0 z-20 bg-slate-900/95 backdrop-blur-sm flex flex-col" data-testid="chat-participants-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h4 className="text-slate-900 font-bold text-sm">In this chat · {parts.length}</h4>
        <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50" data-testid="chat-participants-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
        {parts.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-200" data-testid={`chat-participant-${p.id}`}>
            <div className="min-w-0">
              <span className="text-sm text-slate-800">{p.name}{p.id === myId && <span className="text-slate-500"> (you)</span>}</span>
              <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500">{ROLE_LABEL[p.role] || p.role}</span>
            </div>
            {isMgmt && (
              <button onClick={() => remove(p)} disabled={busy === p.id} className="p-1.5 rounded-lg text-slate-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-40" title="Remove from chat" data-testid={`chat-participant-remove-${p.id}`}>
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
              </button>
            )}
          </div>
        ))}
        {isMgmt && (
          <div className="pt-3" data-testid="chat-add-person">
            <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1.5">Add someone</p>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" className="w-full pl-8 pr-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500" data-testid="chat-add-search" />
            </div>
            <div className="mt-1.5 space-y-0.5">
              {people === null ? <p className="text-xs text-slate-500 py-2">Loading…</p>
                : candidates.length === 0 ? <p className="text-xs text-slate-500 py-2">{q ? 'No one matches — or they’re already in the chat.' : 'Type a name to find someone.'}</p>
                : candidates.map((u) => (
                  <button key={u.id} onClick={() => add(u)} disabled={busy === u.id} className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 text-left disabled:opacity-40" data-testid={`chat-add-${u.id}`}>
                    <span className="text-sm text-slate-800">{u.name} <span className="text-[10px] uppercase tracking-wide text-slate-500 ml-1">{ROLE_LABEL[u.role] || u.role}</span></span>
                    {busy === u.id ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : <UserPlus className="w-4 h-4 text-emerald-700" />}
                  </button>))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
